/**
 * Phosphor Icons Data Loader
 * Loads Phosphor icon JSON data at build time (no async fetch).
 *
 * Output is generated by `scripts/build-phosphor-icons.ts` into:
 * - public/phosphor-nodes-regular.json
 * - public/phosphor-nodes-fill.json
 *
 * Format: Record<string, Array<["path", { d: string }]>>
 */

type PhosphorPathNode = ['path', { d: string }];
type PhosphorIconData = PhosphorPathNode[];
type PhosphorIconsMap = Record<string, PhosphorIconData>;

// Project-relative `public/` assets (works in Vite dev/build and Vitest; avoid root `/…` URLs in tests).
import phosphorNodesRegularUrl from '../../public/phosphor-nodes-regular.json?url';
import phosphorNodesFillUrl from '../../public/phosphor-nodes-fill.json?url';

let phosphorNodesRegularData: PhosphorIconsMap | null = null;
let phosphorNodesFillData: PhosphorIconsMap | null = null;

/**
 * Kept as async for API compatibility with entrypoints.
 * Data is loaded on demand from `public/` and cached.
 */
export async function loadPhosphorIconData(): Promise<void> {
  if (phosphorNodesRegularData && phosphorNodesFillData) return;

  const [regularRes, fillRes] = await Promise.all([
    fetch(phosphorNodesRegularUrl),
    fetch(phosphorNodesFillUrl),
  ]);

  if (!regularRes.ok || !fillRes.ok) {
    // Leave caches null; callers will gracefully return empty maps.
    return;
  }

  const [regularJson, fillJson] = (await Promise.all([
    regularRes.json(),
    fillRes.json(),
  ])) as [unknown, unknown];

  phosphorNodesRegularData = (regularJson as PhosphorIconsMap) ?? {};
  phosphorNodesFillData = (fillJson as PhosphorIconsMap) ?? {};
}

export function getPhosphorNodesOutline(): PhosphorIconsMap {
  return phosphorNodesRegularData ?? {};
}

export function getPhosphorNodesFilled(): PhosphorIconsMap {
  return phosphorNodesFillData ?? {};
}
